import SEO from '@/components/SEO';
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { useQuoteDrawer } from '../../contexts/QuoteDrawerContext';
import { Cloud, Zap, Shield, Monitor } from "lucide-react";
import { RelatedServices } from '@/components/RelatedServices';
import { Smartphone, PenTool } from "lucide-react";
import WebDesignLocations from '@/components/WebDesignLocations';

export default function CloudServices() {
  const { openDrawer } = useQuoteDrawer();

  const services = [
    {
      title: "Cloud Migration",
      description: "Seamless migration of your applications and data to cloud infrastructure with minimal downtime.",
      icon: <Cloud className="h-8 w-8" />
    },
    {
      title: "DevOps Implementation",
      description: "Streamline development and deployment processes with CI/CD pipelines and automation.",
      icon: <Zap className="h-8 w-8" />
    },
    {
      title: "Cloud Security",
      description: "Comprehensive security solutions to protect your cloud infrastructure and data.",
      icon: <Shield className="h-8 w-8" />
    },
    {
      title: "Monitoring & Optimization",
      description: "24/7 monitoring and performance optimization to ensure optimal cloud performance.",
      icon: <Monitor className="h-8 w-8" />
    }
  ];

  const cloudPlatforms = [
    { name: "AWS", icon: "fab fa-aws", description: "Amazon Web Services" },
    { name: "Azure", icon: "fab fa-microsoft", description: "Microsoft Azure" },
    { name: "Google Cloud", icon: "fab fa-google", description: "Google Cloud Platform" },
    { name: "DigitalOcean", icon: "fas fa-water", description: "DigitalOcean Cloud" }
  ];

  const devopsTools = [
    { name: "Docker", icon: "fab fa-docker" },
    { name: "Kubernetes", icon: "fas fa-dharmachakra" },
    { name: "Jenkins", icon: "fas fa-tools" },
    { name: "GitLab", icon: "fab fa-gitlab" },
    { name: "Terraform", icon: "fas fa-layer-group" },
    { name: "Ansible", icon: "fas fa-cogs" }
  ];

  return (
    <>
      <SEO 
        title="Cloud Services UAE | Kecly"
        description="Expert cloud computing solutions in UAE including migration, management and optimization since 2017."
        keywords="cloud services uae, aws azure dubai, cloud migration sharjah"
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-purple-50 to-gray-50 py-24 px-4 sm:px-6 lg:px-8 text-gray-900 overflow-hidden">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-7xl mx-auto text-center relative z-10"
        >
          <h1 className="text-4xl md:text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600 mb-6">
            Cloud Services
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto mb-8">
            Scalable, secure, and high-performance cloud solutions tailored to your business needs
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button 
              onClick={openDrawer}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg font-medium rounded-lg transition-all duration-300 transform hover:scale-105"
            >
              Get a Quote
            </Button>
            <Button 
              asChild
              variant="outline" 
              className="border-gray-300 text-gray-700 hover:bg-gray-100 px-8 py-3 text-lg font-medium rounded-lg transition-all duration-300"
            >
              <Link href="/contact">
                Contact Us
              </Link>
            </Button>
          </div>
        </motion.div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-64 h-64 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Cloud & DevOps Services</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Comprehensive cloud solutions to modernize your infrastructure
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-effect p-8 rounded-xl"
              >
                <div className="w-16 h-16 bg-primary/20 rounded-lg flex items-center justify-center mb-6 text-primary">
                  {service.icon}
                </div>
                <h3 className="font-orbitron font-semibold text-xl mb-4">{service.title}</h3>
                <p className="text-muted-foreground">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Cloud Platforms */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Cloud Platforms</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              We work with all major cloud providers to find the best fit for your needs
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {cloudPlatforms.map((platform, index) => (
              <motion.div
                key={platform.name}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-effect p-8 rounded-xl text-center"
              >
                <i className={`${platform.icon} text-5xl text-primary mb-4`}></i>
                <h3 className="font-orbitron font-semibold text-lg mb-2">{platform.name}</h3>
                <p className="text-muted-foreground text-sm">{platform.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DevOps Tools */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">DevOps Technologies</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Modern tools and practices for efficient development and deployment
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            {devopsTools.map((tool, index) => (
              <motion.div
                key={tool.name}
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="glass-effect p-6 rounded-xl text-center"
              >
                <i className={`${tool.icon} text-4xl text-primary mb-4`}></i>
                <p className="font-orbitron text-sm">{tool.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Cloud Benefits</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Transform your business with cloud-first approach
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Cost Optimization",
                description: "Reduce infrastructure costs with pay-as-you-use cloud resources",
                icon: "fas fa-dollar-sign"
              },
              {
                title: "Scalability",
                description: "Scale resources up or down based on demand automatically",
                icon: "fas fa-expand-arrows-alt"
              },
              {
                title: "High Availability",
                description: "Ensure 99.9% uptime with redundant cloud infrastructure",
                icon: "fas fa-check-circle"
              }
            ].map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-primary to-accent rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${benefit.icon} text-xl text-primary-foreground`}></i>
                </div>
                <h3 className="font-orbitron font-semibold text-xl mb-4">{benefit.title}</h3>
                <p className="text-muted-foreground">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Migration Process */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Migration Process</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Seamless cloud migration with minimal business disruption
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Assessment", description: "Evaluate current infrastructure and requirements" },
              { step: "02", title: "Planning", description: "Design migration strategy and timeline" },
              { step: "03", title: "Migration", description: "Execute migration with minimal downtime" },
              { step: "04", title: "Optimization", description: "Monitor and optimize cloud performance" }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-primary to-accent rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="font-orbitron font-bold text-primary-foreground">{item.step}</span>
                </div>
                <h3 className="font-orbitron font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Web Design Locations */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <WebDesignLocations />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-50">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-orbitron font-bold text-4xl text-gray-900 mb-6">
              Ready for the Cloud?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Accelerate your digital transformation with modern cloud infrastructure and DevOps practices
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={openDrawer} 
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg font-medium rounded-lg"
              >
                Start Cloud Journey
              </Button>
              <Link href="/contact">
                <Button 
                  variant="outline"
                  className="border-gray-300 text-gray-700 hover:bg-gray-100 px-8 py-3 text-lg font-medium rounded-lg"
                >
                  Get Cloud Assessment
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <RelatedServices 
            currentService="Cloud Services" 
            services={[
              // ...
            ]} 
          />
        </div>
      </section>

      {/* FAQ Section for SEO */}
      <section className="faq-section bg-white py-12">
        <div className="container mx-auto px-6">
          <h2 className="text-2xl font-bold mb-4">Frequently Asked Questions</h2>
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold">What is cloud migration?</h3>
              <p>Cloud migration is the process of moving your data, applications, and workloads from on-premises infrastructure to cloud platforms like AWS, Azure, or Google Cloud for greater flexibility and cost savings.</p>
            </div>
            <div>
              <h3 className="font-semibold">Which cloud platforms do you support?</h3>
              <p>We support AWS, Microsoft Azure, Google Cloud, and DigitalOcean, and can help you choose the best fit for your business needs.</p>
            </div>
            <div>
              <h3 className="font-semibold">How secure is the cloud?</h3>
              <p>We implement robust cloud security measures, including encryption, access controls, and continuous monitoring, to keep your data safe.</p>
            </div>
            <div>
              <h3 className="font-semibold">Do you offer ongoing cloud management?</h3>
              <p>Yes, we provide 24/7 monitoring, optimization, and support to ensure your cloud infrastructure runs smoothly.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Internal Links for SEO */}
      <section className="internal-links-section bg-gray-50 py-8">
        <div className="container mx-auto px-6 text-center">
          <span className="font-semibold">Explore more:</span>
          <div className="flex flex-wrap gap-4 justify-center mt-2">
            <Link href="/services" className="underline text-blue-700">All Services</Link>
            <Link href="/maintenance-support" className="underline text-blue-700">Maintenance & Support</Link>
            <Link href="/contact" className="underline text-blue-700">Contact Us</Link>
            <Link href="/portfolio" className="underline text-blue-700">Portfolio</Link>
          </div>
        </div>
      </section>
    </>
  );
}
